import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import { useBusinessData } from '../../context/BusinessContext';
import '../../css/BusinessInfo.css';

const BusinessInfo = () => {
  const { loading, error, getData, hasData } = useBusinessData();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  // Check if any legal info exists and has content
  const legalInfo = getData('legalInfo', {});
  const hasLegalContent = [
    legalInfo.businessType,
    legalInfo.cinNumber,
    legalInfo.gstin,
    legalInfo.panNumber,
    legalInfo.tanNumber,
    legalInfo.fssaiLicense,
    legalInfo.importExportCode,
    legalInfo.registrationNumber,
    (legalInfo.udyamRegistration && legalInfo.udyamRegistration.number),
    ...(legalInfo.customFields || []).map(f => f.value)
  ].some(val => val && typeof val === 'string' && val.trim() !== '');

  // If no legal content AND no banking details AND no certifications, don't render anything
  // (Assuming this component is the main wrapper for all valid business info)
  // Actually, keeping strict to "Business Information" section (legal info) logic inside the render.
  
  // If we want to hide specifically the "Business Information" header if no legal info:
  // We can keep the component but hide the header/grid if hasLegalContent is false.
  // But wait, banking details and certifications are also in this component.
  // If hasLegalContent is false, we should just not render the "Business Information" header and grid.
  
  const showLegalSection = hasData('legalInfo') && hasLegalContent;
  const showBankingSection = hasData('bankingDetails') && (
    getData('bankingDetails.accountName') || 
    getData('bankingDetails.accountNumber') || 
    getData('bankingDetails.ifscCode')
  );
  const showCertifications = hasData('about.certifications') && getData('about.certifications', []).length > 0;

  if (!showLegalSection && !showBankingSection && !showCertifications) {
    return null;
  }

  return (
    <div className="business-legal-info">
      {showLegalSection && (
        <>
          <h2>{getData('ui.businessInfo.sectionTitle', 'Business Information')}</h2>
          
          <div className="info-grid">
            {/* Business Type */}
        {hasData('legalInfo.businessType') && getData('legalInfo.businessType') && (
          <div className="info-item">
            <div className="info-icon">
              <FontAwesomeIcon icon={solidIcons.faBuilding} />
            </div>
            <div className="info-content">
              <h3>Business Type</h3>
              <p>{getData('legalInfo.businessType')}</p>
            </div>
          </div>
        )}
        
        {/* Registration Number / CIN */}
        {hasData('legalInfo.cinNumber') && getData('legalInfo.cinNumber') && (
          <div className="info-item">
            <div className="info-icon">
              <FontAwesomeIcon icon={solidIcons.faIdCard} />
            </div>
            <div className="info-content">
              <h3>CIN</h3>
              <p>{getData('legalInfo.cinNumber')}</p>
            </div>
          </div>
        )}

        {/* Registration Number (General) */}
        {hasData('legalInfo.registrationNumber') && getData('legalInfo.registrationNumber') && (
          <div className="info-item">
            <div className="info-icon">
              <FontAwesomeIcon icon={solidIcons.faRegistered} />
            </div>
            <div className="info-content">
              <h3>Registration No.</h3>
              <p>{getData('legalInfo.registrationNumber')}</p>
            </div>
          </div>
        )}
        
        {/* GSTIN */}
        {hasData('legalInfo.gstin') && getData('legalInfo.gstin') && (
          <div className="info-item">
            <div className="info-icon">
              <FontAwesomeIcon icon={solidIcons.faFileAlt} />
            </div>
            <div className="info-content">
              <h3>GSTIN</h3>
              <p>{getData('legalInfo.gstin')}</p>
            </div>
          </div>
        )}
        
        {/* PAN Number */}
        {hasData('legalInfo.panNumber') && getData('legalInfo.panNumber') && (
          <div className="info-item">
            <div className="info-icon">
              <FontAwesomeIcon icon={solidIcons.faIdCard} />
            </div>
            <div className="info-content">
              <h3>PAN Number</h3>
              <p>{getData('legalInfo.panNumber')}</p>
            </div>
          </div>
        )}

        {/* TAN Number */}
        {hasData('legalInfo.tanNumber') && getData('legalInfo.tanNumber') && (
          <div className="info-item">
            <div className="info-icon">
              <FontAwesomeIcon icon={solidIcons.faFileInvoice} />
            </div>
            <div className="info-content">
              <h3>TAN Number</h3>
              <p>{getData('legalInfo.tanNumber')}</p>
            </div>
          </div>
        )}
        
        {/* Udyam Registration */}
        {hasData('legalInfo.udyamRegistration') && getData('legalInfo.udyamRegistration.number') && (
          <div className="info-item">
            <div className="info-icon">
              <FontAwesomeIcon icon={solidIcons.faCertificate} />
            </div>
            <div className="info-content">
              <h3>Udyam Registration</h3>
              <p><strong>Number:</strong> {getData('legalInfo.udyamRegistration.number')}</p>
              {hasData('legalInfo.udyamRegistration.registrationDate') && getData('legalInfo.udyamRegistration.registrationDate') && (
                <p><strong>Date:</strong> {getData('legalInfo.udyamRegistration.registrationDate')}</p>
              )}
              {hasData('legalInfo.udyamRegistration.category') && getData('legalInfo.udyamRegistration.category') && (
                <p><strong>Category:</strong> {getData('legalInfo.udyamRegistration.category')}</p>
              )}
            </div>
          </div>
        )}
        
        {/* Import Export Code */}
        {hasData('legalInfo.importExportCode') && getData('legalInfo.importExportCode') && (
          <div className="info-item">
            <div className="info-icon">
              <FontAwesomeIcon icon={solidIcons.faGlobe} />
            </div>
            <div className="info-content">
              <h3>IEC</h3>
              <p>{getData('legalInfo.importExportCode')}</p>
            </div>
          </div>
        )}
        
        {/* FSSAI License */}
        {hasData('legalInfo.fssaiLicense') && getData('legalInfo.fssaiLicense') && (
          <div className="info-item">
            <div className="info-icon">
              <FontAwesomeIcon icon={solidIcons.faUtensils} />
            </div>
            <div className="info-content">
              <h3>FSSAI License</h3>
              <p>{getData('legalInfo.fssaiLicense')}</p>
            </div>
          </div>
        )}

        {/* Custom Legal Fields */}
        {hasData('legalInfo.customFields') && getData('legalInfo.customFields', []).map((field, index) => (
          field.label && field.value && (
            <div className="info-item" key={`custom-${index}`}>
              <div className="info-icon">
                <FontAwesomeIcon icon={field.icon && solidIcons[field.icon] ? solidIcons[field.icon] : solidIcons.faCertificate} />
              </div>
              <div className="info-content">
                <h3>{field.label}</h3>
                <p>{field.value}</p>
              </div>
            </div>
          )
        ))}
      </div>
      </>
    )}

      {/* Banking Details Section - Optional, only show when banking details are available */}
      {hasData('bankingDetails') && (
        <div className="banking-details">
          <h3>{getData('ui.businessInfo.bankingTitle', 'Banking Details')}</h3>
          <div className="info-grid">
            {hasData('bankingDetails.accountName') && (
              <div className="info-item">
                <div className="info-icon">
                  <FontAwesomeIcon icon={solidIcons.faCreditCard} />
                </div>
                <div className="info-content">
                  <h4>Account Name</h4>
                  <p>{getData('bankingDetails.accountName')}</p>
                </div>
              </div>
            )}
            
            {hasData('bankingDetails.bankName') && (
              <div className="info-item">
                <div className="info-icon">
                  <FontAwesomeIcon icon={solidIcons.faBuilding} />
                </div>
                <div className="info-content">
                  <h4>Bank</h4>
                  <p>{getData('bankingDetails.bankName')}, {getData('bankingDetails.branchName', '')}</p>
                </div>
              </div>
            )}
            
            {hasData('bankingDetails.accountNumber') && (
              <div className="info-item">
                <div className="info-icon">
                  <FontAwesomeIcon icon={solidIcons.faIdCard} />
                </div>
                <div className="info-content">
                  <h4>Account Number</h4>
                  <p>{getData('bankingDetails.accountNumber')}</p>
                </div>
              </div>
            )}
            
            {hasData('bankingDetails.ifscCode') && (
              <div className="info-item">
                <div className="info-icon">
                  <FontAwesomeIcon icon={solidIcons.faIdCard} />
                </div>
                <div className="info-content">
                  <h4>IFSC Code</h4>
                  <p>{getData('bankingDetails.ifscCode')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Certifications Section */}
      {hasData('about.certifications') && getData('about.certifications').length > 0 && (
        <div className="certifications">
          <h3>{getData('ui.businessInfo.certificationsTitle', 'Certifications')}</h3>
          <div className="cert-grid">
            {getData('about.certifications', []).map((cert, index) => (
              <div className="cert-item" key={index}>
                <div className="cert-header">
                  <div className="cert-icon">
                    <FontAwesomeIcon icon={solidIcons.faAward} />
                  </div>
                  <h4 className="cert-title">{cert.name}</h4>
                </div>
                <div className="cert-content">
                  {cert.description && (
                    <p className="cert-description">{cert.description}</p>
                  )}
                  
                  <div className="cert-details-container">
                    {cert.issuedBy && (
                      <div className="cert-detail">
                        <strong>Issued By:</strong>
                        <p>{cert.issuedBy}</p>
                      </div>
                    )}
                    
                    {cert.issueDate && (
                      <div className="cert-detail">
                        <strong>Issue Date:</strong>
                        <p>{cert.issueDate}</p>
                      </div>
                    )}
                    
                    {cert.validUntil && (
                      <div className="cert-detail">
                        <strong>Valid Until:</strong>
                        <p>{cert.validUntil}</p>
                      </div>
                    )}
                    
                    {cert.registrationNumber && (
                      <div className="cert-detail">
                        <strong>Registration Number:</strong>
                        <p>{cert.registrationNumber}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessInfo; 